"use client";

import { useState } from "react";
import { FiClock } from "react-icons/fi";
import Button from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { formatDateTime } from "@/lib/format-data";
import { toast } from "@/lib/use-toast";
import { useScheduleMessageMutation } from "@/query/comms/chat";
interface ScheduleMessageDialogProps {
  chatId: string;
  content: string;
  disabled?: boolean;
  onScheduled?: () => void;
}
export function ScheduleMessageDialog({
  chatId,
  content,
  disabled,
  onScheduled
}: ScheduleMessageDialogProps) {
  const clock = useClinicClock();
  const [open, setOpen] = useState(false);
  const [sendAt, setSendAt] = useState<Date | undefined>(undefined);
  const scheduleMutation = useScheduleMessageMutation();
  const openDialog = () => {
    // Default to 15 minutes from now so the pre-filled time stays comfortably
    // in the future — it survives a slow save (no future-time-guard rejection)
    // and matches the ~5-minute sweep cadence that dispatches scheduled texts.
    const next = new Date();
    next.setSeconds(0, 0);
    next.setMinutes(next.getMinutes() + 15);
    setSendAt(next);
    setOpen(true);
  };
  const handleSchedule = async () => {
    const trimmed = content.trim();
    if (!trimmed || !sendAt) return;
    if (sendAt <= new Date()) {
      toast({
        title: "Pick a future time",
        description: "The scheduled time must be in the future.",
        variant: "error"
      });
      return;
    }
    try {
      await scheduleMutation.mutateAsync({
        chatId,
        content: trimmed,
        sendAt
      });
      toast({
        title: "Message scheduled",
        description: `Sending on ${formatDateTime(sendAt)}.`,
        variant: "success"
      });
      setOpen(false);
      setSendAt(undefined);
      onScheduled?.();
    } catch {
      toast({
        title: "Could not schedule message",
        description: "Please try again.",
        variant: "error"
      });
    }
  };
  return <>
      <button type="button" onClick={openDialog} disabled={disabled} aria-label="Schedule send" className="shrink-0 text-primary disabled:cursor-not-allowed disabled:text-foreground-muted">
        <FiClock size={20} />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule text</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="rounded-lg border border-border bg-muted px-3 py-2 text-body text-foreground">
              {content.trim() || "No message content"}
            </div>
            <div className="space-y-1">
              <label className="text-caption text-foreground-secondary">
                Send at
              </label>
              <DatePicker value={sendAt} onChange={setSendAt} showTimePicker timeZone={clock.timeZone}
            // Days before the clinic's today; the future-time check
            // happens on submit.
            disabledDays={{
              before: clock.todayMarker()
            }} enableYearNavigation placeholder="Pick a date and time" />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSchedule} disabled={!sendAt || !content.trim()} isLoading={scheduleMutation.isPending}>
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>;
}